import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { auth } from '../firebase';
import { getUserProfile, getScheduledActivitiesForMonth, addSingleActivity, getCurrentProgram } from '../utils/database';
import { onAuthStateChanged } from 'firebase/auth';
import '../styles/Dashboard.css';
import '../styles/Calendar.css';
import ChevronLeftDoubleIcon from '../assets/icons/chevron-left-double.svg';
import ChevronRightDoubleIcon from '../assets/icons/chevron-right-double.svg';
import QuickAddActivityModal from '../components/QuickAddActivityModal';
import DayDetailsModal from '../components/DayDetailsModal';
import DashboardSidebar from '../components/DashboardSidebar';
import BottomNav from '../components/BottomNav';

// Helper to format date as YYYY-MM-DD in local time
const formatLocalDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

function Calendar() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [scheduledActivities, setScheduledActivities] = useState([]);
  const [loadingActivities, setLoadingActivities] = useState(true);
  
  // Program data state
  const [programData, setProgramData] = useState(null);
  
  // Quick Add Activity Modal state
  const [isQuickAddModalOpen, setIsQuickAddModalOpen] = useState(false);
  const [selectedModalDate, setSelectedModalDate] = useState(null);
  
  // Day Details Modal state
  const [isDayDetailsModalOpen, setIsDayDetailsModalOpen] = useState(false);
  const [selectedDayDate, setSelectedDayDate] = useState(null);
  const [selectedDayActivities, setSelectedDayActivities] = useState([]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      try {
        if (!currentUser || currentUser.uid !== userId) {
          navigate('/signin');
          return;
        }

        const userData = await getUserProfile(userId);
        if (!userData) {
          navigate('/onboarding');
          return;
        }

        setUser(userData);
        
        // Load program data
        const program = await getCurrentProgram(userId);
        if (program) {
          setProgramData(program);
        }
        
        setLoading(false);
      } catch (error) {
        console.error('[Calendar] Error loading user:', error);
        navigate('/signin');
      }
    });

    return () => unsubscribe();
  }, [userId, navigate]);

  // Fetch scheduled activities when user or month changes
  useEffect(() => {
    const loadActivities = async () => {
      if (!user) return;
      
      setLoadingActivities(true);
      try {
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();
        const activities = await getScheduledActivitiesForMonth(userId, year, month);
        setScheduledActivities(activities);
        setTimeout(() => {
          setLoadingActivities(false);
        }, 100);
      } catch (error) {
        console.error('[Calendar] Error loading activities:', error);
        setLoadingActivities(false);
      }
    };

    loadActivities();
  }, [user, userId, currentDate]);

  // Calculate time progress helper
  const calculateTimeProgress = (startDate, endDate) => {
    if (!startDate || !endDate) return 0;
    
    let start, end;
    try {
      if (startDate && typeof startDate === 'object') {
        if (typeof startDate.toDate === 'function') {
          start = startDate.toDate();
        } else if (startDate.seconds !== undefined) {
          start = new Date(startDate.seconds * 1000 + (startDate.nanoseconds || 0) / 1000000);
        } else if (startDate instanceof Date) {
          start = startDate;
        } else {
          start = new Date(startDate);
        }
      } else if (typeof startDate === 'string') {
        start = new Date(startDate + 'T00:00:00');
      } else {
        start = new Date(startDate);
      }
      
      if (endDate && typeof endDate === 'object') {
        if (typeof endDate.toDate === 'function') {
          end = endDate.toDate();
        } else if (endDate.seconds !== undefined) {
          end = new Date(endDate.seconds * 1000 + (endDate.nanoseconds || 0) / 1000000);
        } else if (endDate instanceof Date) {
          end = endDate;
        } else {
          end = new Date(endDate);
        }
      } else if (typeof endDate === 'string') {
        end = new Date(endDate + 'T00:00:00');
      } else {
        end = new Date(endDate);
      }
      
      if (isNaN(start.getTime()) || isNaN(end.getTime())) return 0;
      
      const now = new Date();
      const total = end - start;
      const elapsed = now - start;
      
      if (total <= 0) return 0;
      return Math.min(100, Math.max(0, (elapsed / total) * 100));
    } catch (error) {
      return 0;
    }
  };

  const calculateTimeRemaining = (endDate) => {
    if (!endDate) return 'N/A';
    
    try {
      let end;
      if (endDate && typeof endDate === 'object') {
        if (typeof endDate.toDate === 'function') {
          end = endDate.toDate();
        } else if (endDate.seconds !== undefined) {
          end = new Date(endDate.seconds * 1000 + (endDate.nanoseconds || 0) / 1000000);
        } else if (endDate instanceof Date) {
          end = endDate;
        } else {
          end = new Date(endDate);
        }
      } else if (typeof endDate === 'string') {
        end = new Date(endDate + 'T00:00:00');
      } else {
        end = new Date(endDate);
      }
      
      if (isNaN(end.getTime())) return 'N/A';
      
      const now = new Date();
      const diffMs = end - now;
      const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
      if (diffDays <= 0) return 'Complete';
      if (diffDays < 7) return `${diffDays} days`;
      if (diffDays < 30) return `${Math.ceil(diffDays / 7)} weeks`;
      return `${Math.ceil(diffDays / 30)} months`;
    } catch (error) {
      return 'N/A';
    }
  };

  // Macrocycle data
  const macrocycleData = useMemo(() => {
    let timeRemaining = 'N/A';
    let name = 'No Active Macrocycle';
    
    if (programData?.macrocycle) {
      name = programData.macrocycle.name || 'No Active Macrocycle';
      
      if (programData.macrocycle.startDate && programData.macrocycle.endDate) {
        timeRemaining = calculateTimeRemaining(programData.macrocycle.endDate);
      } else if (programData.mesocycle?.startDate && programData.mesocycle?.endDate) {
        timeRemaining = calculateTimeRemaining(programData.mesocycle.endDate);
      }
    }
    
    return { timeRemaining, name };
  }, [programData]);

  // Calculate macrocycle days for visualization
  const macrocycleDays = useMemo(() => {
    if (!programData?.macrocycle && !programData?.mesocycle) {
      return [];
    }
    
    let startDate, endDate;
    if (programData?.macrocycle?.startDate && programData?.macrocycle?.endDate) {
      startDate = programData.macrocycle.startDate;
      endDate = programData.macrocycle.endDate;
    } else if (programData?.mesocycle?.startDate && programData?.mesocycle?.endDate) {
      startDate = programData.mesocycle.startDate;
      endDate = programData.mesocycle.endDate;
    } else {
      return [];
    }
    
    let start, end;
    try {
      if (startDate && typeof startDate === 'object') {
        if (typeof startDate.toDate === 'function') {
          start = startDate.toDate();
        } else if (startDate.seconds !== undefined) {
          start = new Date(startDate.seconds * 1000 + (startDate.nanoseconds || 0) / 1000000);
        } else {
          start = new Date(startDate);
        }
      } else if (typeof startDate === 'string') {
        start = new Date(startDate + 'T00:00:00');
      } else {
        start = new Date(startDate);
      }
      
      if (endDate && typeof endDate === 'object') {
        if (typeof endDate.toDate === 'function') {
          end = endDate.toDate();
        } else if (endDate.seconds !== undefined) {
          end = new Date(endDate.seconds * 1000 + (endDate.nanoseconds || 0) / 1000000);
        } else {
          end = new Date(endDate);
        }
      } else if (typeof endDate === 'string') {
        end = new Date(endDate + 'T00:00:00');
      } else {
        end = new Date(endDate);
      }
      
      if (isNaN(start.getTime()) || isNaN(end.getTime())) {
        return [];
      }
    } catch (error) {
      return [];
    }
    
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    const days = [];
    const currentDate = new Date(start);
    currentDate.setHours(0, 0, 0, 0);
    const endDateOnly = new Date(end);
    endDateOnly.setHours(0, 0, 0, 0);
    
    let dayIndex = 0;
    while (currentDate <= endDateOnly) {
      const dayOfWeek = currentDate.getDay();
      const isWeekStart = dayOfWeek === 0 && dayIndex > 0;
      
      let status = 'empty';
      if (currentDate < now) {
        status = 'full';
      } else if (currentDate.getTime() === now.getTime()) {
        status = 'half';
      } else {
        status = 'empty';
      }
      
      days.push({
        date: new Date(currentDate),
        status,
        dayIndex,
        isWeekStart
      });
      
      currentDate.setDate(currentDate.getDate() + 1);
      dayIndex++;
    }
    
    return days;
  }, [programData]);

  // Mesocycle data
  let mesocycleProgress = 0;
  let mesocycleWeeks = 6;
  let mesocycleName = 'No Active Mesocycle';
  
  if (programData?.mesocycle) {
    mesocycleName = programData.mesocycle.name || 'Current Mesocycle';
    mesocycleWeeks = programData.mesocycle.totalWeeks || 6;
    
    if (programData.mesocycle.startDate && programData.mesocycle.endDate) {
      mesocycleProgress = calculateTimeProgress(programData.mesocycle.startDate, programData.mesocycle.endDate);
    }
  }

  const currentMonth = currentDate.toLocaleString('default', { month: 'long' });
  const currentYear = currentDate.getFullYear();

  // Generate calendar days for current month
  const firstDayOfMonth = new Date(currentYear, currentDate.getMonth(), 1).getDay();
  const daysInMonth = new Date(currentYear, currentDate.getMonth() + 1, 0).getDate();
  const calendarDates = [];
  
  for (let i = 0; i < firstDayOfMonth; i++) {
    calendarDates.push(null);
  }
  
  for (let i = 1; i <= daysInMonth; i++) {
    calendarDates.push(i);
  }

  const goToPreviousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const convertTimeToMinutes = (timeStr) => {
    if (!timeStr) return 0;
    
    if (timeStr.includes('AM') || timeStr.includes('PM')) {
      const [time, period] = timeStr.split(' ');
      const [hours, minutes] = time.split(':').map(Number);
      let totalMinutes = hours * 60 + minutes;
      if (period === 'PM' && hours !== 12) totalMinutes += 12 * 60;
      if (period === 'AM' && hours === 12) totalMinutes -= 12 * 60;
      return totalMinutes;
    }
    
    const timeMatch = timeStr.match(/(\d{1,2}):(\d{2})/);
    if (timeMatch) {
      const hours = parseInt(timeMatch[1], 10);
      const minutes = parseInt(timeMatch[2], 10);
      return hours * 60 + minutes;
    }
    
    return 0;
  };

  const getDistanceUnit = () => {
    const units = user?.settings?.units || 'imperial';
    return units === 'metric' ? 'km' : 'miles';
  };

  const getActivitiesForDate = (date) => {
    if (!date) return [];
    
    const dateStr = formatLocalDate(new Date(currentDate.getFullYear(), currentDate.getMonth(), date));
    
    const dayActivities = scheduledActivities
      .filter(activity => activity.scheduledDate === dateStr)
      .map(activity => ({
        name: activity.name,
        time: activity.scheduledTime || '',
        duration: activity.estimatedDuration || null
      }))
      .filter(activity => activity.name);
    
    return dayActivities.sort((a, b) => {
      if (!a.time && !b.time) return 0;
      if (!a.time) return 1;
      if (!b.time) return -1;
      const timeA = convertTimeToMinutes(a.time);
      const timeB = convertTimeToMinutes(b.time);
      return timeA - timeB;
    });
  };

  const handleDayClick = (date) => {
    if (!date) return;
    const targetDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), date);
    const activities = getActivitiesForDate(date);
    setSelectedDayDate(targetDate);
    setSelectedDayActivities(activities);
    setIsDayDetailsModalOpen(true);
  };

  const handleAddActivityFromDayDetails = () => {
    setSelectedModalDate(selectedDayDate);
    setIsDayDetailsModalOpen(false);
    setIsQuickAddModalOpen(true);
  };

  const handleSaveActivity = async (activityData) => {
    if (!selectedModalDate) return;
    
    try {
      const dateStr = formatLocalDate(selectedModalDate);
      await addSingleActivity(userId, dateStr, activityData);
      
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth();
      const activities = await getScheduledActivitiesForMonth(userId, year, month);
      setScheduledActivities(activities);
      
      const updatedActivities = getActivitiesForDate(selectedModalDate.getDate());
      setSelectedDayActivities(updatedActivities);
    } catch (error) {
      console.error('[Calendar] Error adding activity:', error);
      alert('Failed to add activity. Please try again.');
    }
  };

  return (
    <>
    <div className="dashboard-page has-bottom-nav">
      {/* Background effects */}
      <div className="dashboard-bg-glow dashboard-bg-glow-1" />
      <div className="dashboard-bg-glow dashboard-bg-glow-2" />
      <div className="dashboard-overlay" />

      {/* Sidebar */}
      <DashboardSidebar userId={userId} user={user} />

      {/* Main content */}
      <div className="dashboard-main">
        {/* Month Header */}
        <div className="dashboard-card calendar-header-card">
          <div className="calendar-header-content">
            <h4 className="calendar-month-title">{currentMonth} {currentYear}</h4>
            <div className="dashboard-calendar-nav">
              <button className="c-ripple dashboard-calendar-nav-button" onClick={goToPreviousMonth}>
                <img src={ChevronLeftDoubleIcon} alt="Previous month" />
              </button>
              <button className="c-ripple dashboard-calendar-nav-button" onClick={goToNextMonth}>
                <img src={ChevronRightDoubleIcon} alt="Next month" />
              </button>
            </div>
          </div>
        </div>

        {/* Calendar Container */}
        <div className="dashboard-card calendar-main-card">
          <div className="dashboard-calendar-grid calendar-full-grid">
            {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(day => (
              <div key={day} className="dashboard-calendar-weekday">{day}</div>
            ))}
            
            {calendarDates.map((date, index) => {
              const activities = getActivitiesForDate(date);
              const isCurrentDay = date === new Date().getDate() && 
                                   currentDate.getMonth() === new Date().getMonth() &&
                                   currentYear === new Date().getFullYear();
              const hasActivities = activities.length > 0;
              
              return (
                <div 
                  key={index} 
                  className={`calendar-day-card ${date === null ? 'empty' : ''} ${isCurrentDay ? 'today' : ''} ${loadingActivities ? 'loading' : 'loaded'} ${hasActivities ? 'has-activities' : ''}`}
                  onClick={() => handleDayClick(date)}
                >
                  {date !== null && (
                    <>
                      <span className="calendar-day-number">{date}</span>
                      
                      {!loadingActivities && hasActivities && (
                        <div className="calendar-activity-dots">
                          {activities.slice(0, 3).map((_, dotIndex) => (
                            <span key={dotIndex} className="calendar-activity-dot" />
                          ))}
                          {activities.length > 3 && (
                            <span className="calendar-activity-more">+{activities.length - 3}</span>
                          )}
                        </div>
                      )}
                      
                      <div className="calendar-day-activities-desktop">
                        {!loadingActivities && activities.length > 0 && (
                          activities.slice(0, 2).map((activity, activityIndex) => (
                            <div key={activityIndex} className="calendar-activity-item-desktop">
                              <span className="calendar-activity-name-desktop">{activity.name}</span>
                            </div>
                          ))
                        )}
                        {!loadingActivities && activities.length > 2 && (
                          <span className="calendar-activity-overflow">+{activities.length - 2} more</span>
                        )}
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Macrocycle & Mesocycle Section */}
        <div className="calendar-program-section">
          {/* Macrocycle Card */}
          <div className="dashboard-card dashboard-macrocycle-card">
            <div className="dashboard-macrocycle-content">
              <div className="dashboard-macrocycle-text-section">
                <h2 className="dashboard-macrocycle-title">Macrocycle</h2>
                <p className="dashboard-macrocycle-subtitle">{macrocycleData.name}</p>
              </div>
              <div className="dashboard-macrocycle-days-container">
                <div className="dashboard-macrocycle-days">
                  {macrocycleDays.length > 0 ? (
                    macrocycleDays.map((day, index) => (
                      <React.Fragment key={index}>
                        {day.isWeekStart && (
                          <div className="dashboard-macrocycle-week-separator" />
                        )}
                        <div 
                          className={`dashboard-macrocycle-day ${day.status}`}
                          title={day.date.toLocaleDateString()}
                        >
                          <div className="dashboard-macrocycle-day-fill" />
                        </div>
                      </React.Fragment>
                    ))
                  ) : (
                    <div className="dashboard-macrocycle-day empty">
                      <div className="dashboard-macrocycle-day-fill" />
                    </div>
                  )}
                </div>
                {macrocycleData.timeRemaining !== 'N/A' && (
                  <div className="dashboard-macrocycle-time-label">{macrocycleData.timeRemaining}</div>
                )}
              </div>
            </div>
          </div>

          {/* Current Mesocycle Card */}
          <div className="dashboard-card dashboard-mesocycle-card">
            <div className="dashboard-mesocycle-header">
              <div>
                <h2 className="dashboard-mesocycle-title">Mesocycle</h2>
                <p className="dashboard-mesocycle-name">{mesocycleName}</p>
              </div>
              <span className="dashboard-mesocycle-percent">{Math.round(mesocycleProgress)}%</span>
            </div>
            <div className="dashboard-progress-bar-container">
              <div className="dashboard-progress-bar">
                <div 
                  className="dashboard-progress-fill" 
                  style={{ 
                    width: `${Math.max(0, Math.min(100, mesocycleProgress))}%`,
                    minWidth: mesocycleProgress > 0 ? '2px' : '0px'
                  }}
                />
              </div>
            </div>
            <div className="dashboard-progress-labels">
              <span className="dashboard-week-label">Week 1</span>
              <span className="dashboard-week-label">Week {mesocycleWeeks}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Day Details Modal */}
      <DayDetailsModal
        isOpen={isDayDetailsModalOpen}
        onClose={() => {
          setIsDayDetailsModalOpen(false);
          setSelectedDayDate(null);
          setSelectedDayActivities([]);
        }}
        date={selectedDayDate}
        activities={selectedDayActivities}
        onAddActivity={handleAddActivityFromDayDetails}
      />

      {/* Quick Add Activity Modal */}
      <QuickAddActivityModal
        isOpen={isQuickAddModalOpen}
        onClose={() => {
          setIsQuickAddModalOpen(false);
          setSelectedModalDate(null);
        }}
        onSave={handleSaveActivity}
        date={selectedModalDate}
        distanceUnit={getDistanceUnit()}
      />
    </div>

    {/* Bottom Navigation */}
    <BottomNav userId={userId} user={user} />
    </>
  );
}

export default Calendar;
